import Cookies from 'js-cookie';

// Helper functie om environment variabelen op te halen
// Kijkt eerst naar runtime config (window.ENV_CONFIG), dan naar build-time config (process.env)
const getEnvVar = (key, defaultValue = '') => {
    // Runtime configuratie (Docker container)
    if (window.ENV_CONFIG && window.ENV_CONFIG[key]) {
        return window.ENV_CONFIG[key];
    }
    // Build-time configuratie (process.env)
    if (process.env[key]) {
        return process.env[key];
    }
    return defaultValue;
};



// Centrale API configuratie voor multi-tenant setup
export const API_CONFIG = {
    // Central backend URL voor auth en proxy
    get centralBackendURL() {
        return getEnvVar('REACT_APP_CENTRAL_BACKEND_URL', 'http://localhost:8000');
    },
    // Legacy support voor directe API verbinding (fallback)
    get legacyBaseURL() {
        return getEnvVar('REACT_APP_API_URL', 'http://localhost:8200');
    },
    get legacyApiKey() {
        return getEnvVar('REACT_APP_API_KEY', '');
    },
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
};

// Helper functie om te checken of proxy mode is ingeschakeld
export const isProxyModeEnabled = () => {
    const proxyValue = getEnvVar('REACT_APP_USE_PROXY', 'false');
    if (!proxyValue) return false;
    
    // Trim whitespace en verwijder eventuele puntkomma's of andere leestekens
    const cleanValue = proxyValue.trim().replace(/[;,]$/, '').toLowerCase();
    return cleanValue === 'true';
};

// Debug functie om environment variables te tonen
export const debugEnvironmentVariables = () => {
    console.log('🔍 Environment Variables Debug:');
    console.log('REACT_APP_USE_PROXY:', getEnvVar('REACT_APP_USE_PROXY'));
    console.log('REACT_APP_USE_PROXY (cleaned):', getEnvVar('REACT_APP_USE_PROXY') ? getEnvVar('REACT_APP_USE_PROXY').trim().replace(/[;,]$/, '').toLowerCase() : 'undefined');
    console.log('REACT_APP_API_URL:', getEnvVar('REACT_APP_API_URL'));
    console.log('REACT_APP_CENTRAL_BACKEND_URL:', getEnvVar('REACT_APP_CENTRAL_BACKEND_URL'));
    console.log('REACT_APP_FORCE_MULTITENANT:', getEnvVar('REACT_APP_FORCE_MULTITENANT'));
    console.log('Runtime config available:', !!window.ENV_CONFIG);
    console.log('Proxy mode enabled:', isProxyModeEnabled());
    console.log('Multi-tenant mode:', isMultiTenantMode());
};

// Helper functie om de juiste base URL te krijgen (directe verbinding naar electrolyzer of legacy)
export const getBaseURL = () => {
    // Debug environment variables
    debugEnvironmentVariables();
    
    const selectedEnvironment = localStorage.getItem('selected_environment');
    
    if (selectedEnvironment) {
        // Multi-tenant mode: gebruik directe verbinding naar electrolyzer
        const savedEnv = localStorage.getItem(`environment_${selectedEnvironment}`);
        if (savedEnv) {
            const environment = JSON.parse(savedEnv);
            let directURL = environment.api_url;
            console.log('Proxy mode enabled:', isProxyModeEnabled());
            console.log('REACT_APP_USE_PROXY value:', getEnvVar('REACT_APP_USE_PROXY'));
            
            // Als proxy mode is ingeschakeld, gebruik REACT_APP_CENTRAL_BACKEND_URL + /api/v1/proxy/{environment_id}
            if (isProxyModeEnabled() && getEnvVar('REACT_APP_CENTRAL_BACKEND_URL')) {
                directURL = `${getEnvVar('REACT_APP_CENTRAL_BACKEND_URL')}/api/v1/proxy/${selectedEnvironment}`;
                console.debug(`🔗 Using proxy URL: ${directURL} for environment ${selectedEnvironment}`);
            } else {
                // Gebruik de originele environment.api_url, niet REACT_APP_API_URL
                console.debug(`🔗 Using direct electrolyzer URL: ${directURL} for environment ${selectedEnvironment}`);
            }
            
            return directURL;
        }
    }
    
    // Legacy mode: directe verbinding (voor development/fallback)
    console.debug(`🔗 Using legacy URL: ${API_CONFIG.legacyBaseURL}`);
    return API_CONFIG.legacyBaseURL;
};

// Helper functie om auth headers te krijgen
export const getAuthHeaders = () => {
    const token = Cookies.get('auth_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Helper functie om user informatie uit JWT token te halen
const getUserInfoFromToken = () => {
    const token = Cookies.get('auth_token');
    if (!token) return null;
    
    try {
        // JWT token bestaat uit 3 delen: header.payload.signature
        const payload = token.split('.')[1];
        const decodedPayload = JSON.parse(atob(payload));
        
        return {
            userId: decodedPayload.sub,
            email: decodedPayload.email,
            name: decodedPayload.name
        };
    } catch (error) {
        console.warn('Could not decode JWT token:', error);
        return null;
    }
};

// Helper functie om complete headers te krijgen
export const getApiHeaders = () => {
    const selectedEnvironment = localStorage.getItem('selected_environment');
    
    if (selectedEnvironment) {
        // Check of we in proxy mode zijn
        if (isProxyModeEnabled()) {
            // Proxy mode: gebruik Bearer token voor central backend authenticatie
            console.debug(`🔑 Using Bearer token for proxy mode with environment ${selectedEnvironment}`);
            
            const headers = {
                ...API_CONFIG.headers,
                ...getAuthHeaders() // Voegt Bearer token toe
            };
            
            // Environment ID zit al in de URL, dus geen X-Environment-ID header nodig
            
            // Voeg user informatie toe voor audit logging
            const userInfo = getUserInfoFromToken();
            if (userInfo) {
                headers['X-Central-User-ID'] = userInfo.userId;
                headers['X-Central-User-Email'] = userInfo.email;
                headers['X-Central-User-Name'] = userInfo.name;
                console.debug(`👤 Adding user info to headers: ${userInfo.email}`);
            }
            
            return headers;
        }
        
        // Normale multi-tenant mode: gebruik electrolyzer API key
        const savedEnv = localStorage.getItem(`environment_${selectedEnvironment}`);
        if (savedEnv) {
            const environment = JSON.parse(savedEnv);
            if (environment.api_key) {
                console.debug(`🔑 Using electrolyzer API key for environment ${selectedEnvironment}`);
                
                const headers = {
                    ...API_CONFIG.headers,
                    'X-API-Key': environment.api_key
                };
                
                // Voeg user informatie toe voor audit logging
                const userInfo = getUserInfoFromToken();
                if (userInfo) {
                    headers['X-Central-User-ID'] = userInfo.userId;
                    headers['X-Central-User-Email'] = userInfo.email;
                    headers['X-Central-User-Name'] = userInfo.name;
                    console.debug(`👤 Adding user info to headers: ${userInfo.email}`);
                }
                
                return headers;
            }
        }
        
        // Fallback naar JWT als geen API key beschikbaar
        console.debug(`🔑 Fallback to JWT token for environment ${selectedEnvironment}`);
        return {
            ...API_CONFIG.headers,
            ...getAuthHeaders()
        };
    } else {
        // Legacy mode: gebruik legacy API key
        console.debug(`🔑 Using legacy API key`);
        return {
            ...API_CONFIG.headers,
            'X-API-Key': API_CONFIG.legacyApiKey
        };
    }
};

// Helper functie om environment-specifieke headers te krijgen
export const getEnvironmentHeaders = (environmentId) => {
    const token = Cookies.get('auth_token');
    
    if (token && environmentId) {
        return {
            ...API_CONFIG.headers,
            'Authorization': `Bearer ${token}`,
            'X-Environment-ID': environmentId
        };
    }
    
    return getApiHeaders();
};

// Validatie functie voor multi-tenant configuratie
export const validateApiConfig = () => {
    const missingVars = [];
    
    if (!getEnvVar('REACT_APP_CENTRAL_BACKEND_URL')) {
        missingVars.push('REACT_APP_CENTRAL_BACKEND_URL');
    }
    
    // Legacy vars zijn optioneel voor backward compatibility
    if (!getEnvVar('REACT_APP_API_URL')) {
        console.info('REACT_APP_API_URL niet ingesteld - alleen multi-tenant mode beschikbaar');
    }
    
    if (missingVars.length > 0) {
        console.warn(`⚠️ Ontbrekende environment variabelen: ${missingVars.join(', ')}`);
        console.warn('Voor multi-tenant functionaliteit zijn deze vereist');
    }
    
    return missingVars.length === 0;
};

// Helper functie om te checken of we in multi-tenant mode zijn
export const isMultiTenantMode = () => {
    // Als REACT_APP_FORCE_MULTITENANT expliciet op false staat, forceer single tenant mode
    if (getEnvVar('REACT_APP_FORCE_MULTITENANT') === 'false') {
        return false;
    }
    
    // Als REACT_APP_FORCE_MULTITENANT expliciet op true staat, forceer multi tenant mode
    if (getEnvVar('REACT_APP_FORCE_MULTITENANT') === 'true') {
        return true;
    }
    
    // Anders bepaal op basis van of er een environment geselecteerd is (legacy behavior)
    return !!localStorage.getItem('selected_environment');
};

// Helper functie om naar legacy mode te schakelen
export const switchToLegacyMode = () => {
    localStorage.removeItem('selected_environment');
    // Clear auth data
    Cookies.remove('auth_token');
    Object.keys(localStorage).forEach(key => {
        if (key.startsWith('environment_')) {
            localStorage.removeItem(key);
        }
    });
}; 